import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Pagination from '../../components/ui/Pagination';
import { bundlesCatalogService } from '../../services/catalogService';
import { bundlePartnerCountService, bundleLinkedPartnersService } from '../../services/bundleProfileService';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

const CATEGORY_OPTIONS = [
  { value: 'security', label: 'Security' },
  { value: 'connectivity', label: 'Connectivity' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'enablement', label: 'Enablement' },
  { value: 'networking', label: 'Networking' },
  { value: 'iot', label: 'IoT' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'general', label: 'General' },
];

const CATEGORY_TABS = [
  { value: '', label: 'All' },
  { value: 'security', label: 'Security' },
  { value: 'connectivity', label: 'Connectivity' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'enablement', label: 'Enablement' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'iot', label: 'IoT' },
  { value: 'general', label: 'General' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'draft', label: 'Draft' },
];

const FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'draft', label: 'Draft' },
];

const VALUE_RANGE_OPTIONS = [
  { value: '', label: 'All Values' },
  { value: 'under50k', label: 'Under OMR 50K' },
  { value: '50k-200k', label: 'OMR 50K – OMR 200K' },
  { value: '200k-500k', label: 'OMR 200K – OMR 500K' },
  { value: 'over500k', label: 'OMR 500K+' },
];

const EMPTY_FORM = {
  bundleName: '',
  description: '',
  bundleCategory: '',
  targetMarket: '',
  estimatedValue: '',
  includedProducts: '',
  includedServices: '',
  referenceUseCases: '',
  catalogStatus: 'active',
};

const PAGE_SIZE = 10;

const getCategoryColor = (cat) => {
  const map = {
    security: 'bg-red-100 text-red-700',
    connectivity: 'bg-cyan-100 text-cyan-700',
    cloud: 'bg-purple-100 text-purple-700',
    enablement: 'bg-green-100 text-green-700',
    networking: 'bg-blue-100 text-blue-700',
    iot: 'bg-teal-100 text-teal-700',
    analytics: 'bg-orange-100 text-orange-700',
    general: 'bg-slate-100 text-slate-600',
  };
  return map?.[cat] || 'bg-slate-100 text-slate-600';
};

const getStatusColor = (status) => {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    sunset: 'bg-amber-100 text-amber-700',
    draft: 'bg-slate-100 text-slate-600',
  };
  return map?.[status] || 'bg-slate-100 text-slate-600';
};

function valueInRange(estimatedValue, range) {
  if (!range || !estimatedValue) return true;
  const text = estimatedValue?.toLowerCase() || '';
  const extractNumbers = (str) => {
    const nums = str?.match(/[\d,]+/g)?.map(n => parseInt(n?.replace(/,/g, ''), 10))?.filter(Boolean) || [];
    return nums;
  };
  const nums = extractNumbers(text);
  const maxVal = nums?.length > 0 ? Math.max(...nums) : 0;
  const minVal = nums?.length > 0 ? Math.min(...nums) : 0;
  if (range === 'under50k') return maxVal < 50000;
  if (range === '50k-200k') return minVal >= 50000 && maxVal <= 200000;
  if (range === '200k-500k') return minVal >= 200000 && maxVal <= 500000;
  if (range === 'over500k') return maxVal > 500000;
  return true;
}

// ── Assign to Partner Modal ────────────────────────────────────────────────

function AssignPartnerModal({ bundle, onClose, onAssigned }) {
  const toast = useToast();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState(null);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const { data, error } = await supabase
          ?.from('partners')
          ?.select('id, legal_name, brand_name, tier, status')
          ?.eq('status', 'approved')
          ?.order('brand_name', { ascending: true });
        if (error) throw error;
        setPartners(data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadPartners();
  }, []);

  const filtered = partners?.filter(p => {
    const name = p?.brand_name || p?.legal_name || '';
    return name?.toLowerCase()?.includes(search?.toLowerCase());
  });

  const handleAssign = async (partnerId) => {
    setAssigning(partnerId);
    try {
      await bundleLinkedPartnersService?.assign(bundle?.id, partnerId);
      toast?.success('Partner assigned to bundle.');
      onAssigned?.();
      onClose();
    } catch (err) {
      toast?.error('Failed to assign partner. They may already be assigned.');
    } finally {
      setAssigning(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-16">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="px-24 py-20 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h3 className="font-bold text-slate-900">Assign to Partner</h3>
            <p className="text-xs text-slate-500 mt-2">{bundle?.bundleName}</p>
          </div>
          <button onClick={onClose} className="p-6 rounded-lg hover:bg-slate-100 transition-colors">
            <Icon name="X" size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="px-24 pt-16 pb-8 flex-shrink-0">
          <input
            className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
            placeholder="Search partners..."
            value={search}
            onChange={e => setSearch(e?.target?.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto px-24 pb-24">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <div className="w-6 h-6 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : filtered?.length === 0 ? (
            <div className="text-center py-24 text-slate-500 text-sm">No active partners found.</div>
          ) : (
            <div className="space-y-8 mt-8">
              {filtered?.map(partner => (
                <div key={partner?.id} className="flex items-center justify-between p-14 rounded-xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50/30 transition-all">
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{partner?.brand_name || partner?.legal_name}</p>
                    {partner?.tier && <p className="text-xs text-slate-500 mt-2">{partner?.tier}</p>}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAssign(partner?.id)}
                    disabled={assigning === partner?.id}
                  >
                    {assigning === partner?.id ? 'Assigning...' : 'Assign'}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function BundlesCatalog() {
  const toast = useToast();
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partnerCounts, setPartnerCounts] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [valueRangeFilter, setValueRangeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [assignModal, setAssignModal] = useState(null);
  const [partnerCountModal, setPartnerCountModal] = useState(null);

  useEffect(() => {
    loadBundles();
  }, []);

  const loadBundles = async () => {
    setLoading(true);
    try {
      const [data, counts] = await Promise.all([
        bundlesCatalogService?.getAll(),
        bundlePartnerCountService?.getAllCounts(),
      ]);
      setBundles(data);
      setPartnerCounts(counts);
    } catch (err) {
      toast?.error('Failed to load bundles catalog.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!formData?.bundleName?.trim()) {
      toast?.error('Bundle name is required.');
      return;
    }
    try {
      if (editingId) {
        const updated = await bundlesCatalogService?.update(editingId, formData);
        setBundles(prev => prev?.map(b => b?.id === editingId ? updated : b));
        toast?.success('Bundle updated in catalog.');
      } else {
        const created = await bundlesCatalogService?.create(formData);
        setBundles(prev => [created, ...prev]);
        toast?.success('Bundle added to catalog.');
      }
      resetForm();
    } catch (err) {
      toast?.error(editingId ? 'Failed to update bundle.' : 'Failed to add bundle.');
      console.error(err);
    }
  };

  const handleEdit = (bundle) => {
    setFormData({
      bundleName: bundle?.bundleName || '',
      description: bundle?.description || '',
      bundleCategory: bundle?.bundleCategory || '',
      targetMarket: bundle?.targetMarket || '',
      estimatedValue: bundle?.estimatedValue || '',
      includedProducts: bundle?.includedProducts || '',
      includedServices: bundle?.includedServices || '',
      referenceUseCases: bundle?.referenceUseCases || '',
      catalogStatus: bundle?.catalogStatus || 'active',
    });
    setEditingId(bundle?.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      await bundlesCatalogService?.delete(id);
      setBundles(prev => prev?.filter(b => b?.id !== id));
      toast?.success('Bundle removed from catalog.');
    } catch (err) {
      toast?.error('Failed to delete bundle.');
      console.error(err);
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleClone = async (bundle) => {
    try {
      const cloneData = {
        bundleName: `${bundle?.bundleName} (Copy)`,
        description: bundle?.description,
        bundleCategory: bundle?.bundleCategory,
        targetMarket: bundle?.targetMarket,
        estimatedValue: bundle?.estimatedValue,
        includedProducts: bundle?.includedProducts,
        includedServices: bundle?.includedServices,
        referenceUseCases: bundle?.referenceUseCases,
        catalogStatus: 'draft',
      };
      const created = await bundlesCatalogService?.create(cloneData);
      setBundles(prev => [created, ...prev]);
      toast?.success('Bundle cloned as draft.');
    } catch (err) {
      toast?.error('Failed to clone bundle.');
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds?.length === 0) return;
    try {
      await Promise.all(
        selectedIds?.map(id => {
          const bundle = bundles?.find(b => b?.id === id);
          if (!bundle) return Promise.resolve();
          return bundlesCatalogService?.update(id, { ...bundle, catalogStatus: bulkStatus });
        })
      );
      setBundles(prev => prev?.map(b => selectedIds?.includes(b?.id) ? { ...b, catalogStatus: bulkStatus } : b));
      toast?.success(`${selectedIds?.length} bundle${selectedIds?.length !== 1 ? 's' : ''} updated to ${bulkStatus}.`);
      setSelectedIds([]);
      setBulkStatus('');
    } catch (err) {
      toast?.error('Failed to update bundle statuses.');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const filtered = bundles?.filter(b => {
    const matchSearch = !search ||
      b?.bundleName?.toLowerCase()?.includes(search?.toLowerCase()) ||
      b?.description?.toLowerCase()?.includes(search?.toLowerCase()) ||
      b?.targetMarket?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = !statusFilter || b?.catalogStatus === statusFilter;
    const matchCategory = !categoryFilter || b?.bundleCategory === categoryFilter;
    const matchValue = valueInRange(b?.estimatedValue, valueRangeFilter);
    return matchSearch && matchStatus && matchCategory && matchValue;
  });

  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds?.length === paginated?.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginated?.map(b => b?.id));
    }
  };

  return (
    <>
      <Helmet>
        <title>Bundles Catalog - PartnerHub</title>
        <meta name="description" content="Master catalog of solution bundles for partner portfolio mapping" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/20 to-purple-50/20">
        <Header />

        {assignModal && (
          <AssignPartnerModal
            bundle={assignModal}
            onClose={() => setAssignModal(null)}
            onAssigned={loadBundles}
          />
        )}

        <main className="pt-[148px] pb-32 px-24 lg:px-32">
          <div className="max-w-[1600px] mx-auto">

            {/* Page Header */}
            <div className="mb-32">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-20 mb-24">
                <div className="flex items-center gap-16">
                  <div className="p-12 bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl shadow-lg">
                    <Icon name="Layers" size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900">Bundles Catalog</h1>
                    <p className="text-slate-500 mt-4">Master catalog of solution bundles — partners select from this list</p>
                  </div>
                </div>
                <Button
                  variant={showForm ? 'outline' : 'default'}
                  iconName={showForm ? 'X' : 'Plus'}
                  iconPosition="left"
                  iconSize={18}
                  onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}
                >
                  {showForm ? 'Cancel' : 'Add Bundle'}
                </Button>
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-16 mb-24">
                {[
                  { label: 'Total Bundles', value: bundles?.length, icon: 'Layers', color: 'text-violet-600' },
                  { label: 'Active', value: bundles?.filter(b => b?.catalogStatus === 'active')?.length, icon: 'CheckCircle2', color: 'text-emerald-600' },
                  { label: 'Sunset', value: bundles?.filter(b => b?.catalogStatus === 'sunset')?.length, icon: 'Sunset', color: 'text-amber-600' },
                  { label: 'Draft', value: bundles?.filter(b => b?.catalogStatus === 'draft')?.length, icon: 'FileEdit', color: 'text-slate-500' },
                ]?.map(stat => (
                  <div key={stat?.label} className="bg-white rounded-xl border border-slate-200 p-16 shadow-sm">
                    <div className="flex items-center gap-10 mb-8">
                      <Icon name={stat?.icon} size={16} className={stat?.color} />
                      <span className="text-xs text-slate-500 font-medium">{stat?.label}</span>
                    </div>
                    <p className={`text-2xl font-bold ${stat?.color}`}>{stat?.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Add / Edit Form */}
            {showForm && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-32 overflow-hidden">
                <div className="px-28 py-20 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-purple-50">
                  <h2 className="text-lg font-bold text-slate-900">
                    {editingId ? 'Edit Bundle' : 'Add New Bundle'}
                  </h2>
                  <p className="text-sm text-slate-500 mt-2">
                    {editingId ? 'Update the master bundle details.' : 'Define a new solution bundle for the master catalog.'}
                  </p>
                </div>
                <div className="p-28 grid grid-cols-1 md:grid-cols-2 gap-20">
                  <div className="md:col-span-2">
                    <Input
                      label="Bundle Name *"
                      placeholder="e.g. Enterprise Security Suite"
                      value={formData?.bundleName}
                      onChange={e => handleField('bundleName', e?.target?.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-6">Description</label>
                    <textarea
                      className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
                      rows={3}
                      placeholder="Describe the solution bundle, its components, and value proposition..."
                      value={formData?.description}
                      onChange={e => handleField('description', e?.target?.value)}
                    />
                  </div>
                  <Select
                    label="Bundle Category"
                    options={[{ value: '', label: 'Select category...' }, ...CATEGORY_OPTIONS]}
                    value={formData?.bundleCategory}
                    onChange={val => handleField('bundleCategory', val)}
                  />
                  <Select
                    label="Catalog Status"
                    options={STATUS_OPTIONS}
                    value={formData?.catalogStatus}
                    onChange={val => handleField('catalogStatus', val)}
                  />
                  <Input
                    label="Target Market"
                    placeholder="e.g. Enterprise (1000+ seats), Government"
                    value={formData?.targetMarket}
                    onChange={e => handleField('targetMarket', e?.target?.value)}
                  />
                  <Input
                    label="Estimated Value"
                    placeholder="e.g. OMR 50,000 – OMR 200,000"
                    value={formData?.estimatedValue}
                    onChange={e => handleField('estimatedValue', e?.target?.value)}
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-6">Included Products</label>
                    <textarea
                      className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
                      rows={2}
                      placeholder="Comma-separated: FortiGate 600E, FortiEndpoint, FortiSIEM"
                      value={formData?.includedProducts}
                      onChange={e => handleField('includedProducts', e?.target?.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-6">Included Services</label>
                    <textarea
                      className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
                      rows={2}
                      placeholder="Comma-separated: Managed SOC, Incident Response, Security Assessment"
                      value={formData?.includedServices}
                      onChange={e => handleField('includedServices', e?.target?.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-6">Reference Use Cases</label>
                    <textarea
                      className="w-full border border-slate-300 rounded-lg px-14 py-10 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400 resize-none"
                      rows={2}
                      placeholder="Describe real-world scenarios where this bundle has been or can be deployed..."
                      value={formData?.referenceUseCases}
                      onChange={e => handleField('referenceUseCases', e?.target?.value)}
                    />
                  </div>
                </div>
                <div className="px-28 py-16 border-t border-slate-100 flex items-center justify-end gap-12">
                  <Button variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button
                    variant="default"
                    iconName={editingId ? 'Save' : 'Plus'}
                    iconPosition="left"
                    iconSize={16}
                    onClick={handleSubmit}
                  >
                    {editingId ? 'Save Changes' : 'Add Bundle'}
                  </Button>
                </div>
              </div>
            )}

            {/* Category Filter Tabs */}
            <div className="flex items-center gap-6 mb-16 overflow-x-auto pb-4">
              {CATEGORY_TABS?.map(tab => (
                <button
                  key={tab?.value}
                  onClick={() => { setCategoryFilter(tab?.value); setPage(1); }}
                  className={`px-16 py-8 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                    categoryFilter === tab?.value
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-violet-300 hover:text-violet-600'
                  }`}
                >
                  {tab?.label}
                  {tab?.value !== '' && (
                    <span className="ml-6 text-xs opacity-70">
                      {bundles?.filter(b => b?.bundleCategory === tab?.value)?.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Filters Row */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-20 mb-24">
              <div className="flex flex-col md:flex-row gap-16">
                <div className="flex-1">
                  <Input
                    placeholder="Search bundles by name, description, or market..."
                    value={search}
                    onChange={e => { setSearch(e?.target?.value); setPage(1); }}
                    className="w-full"
                  />
                </div>
                <div className="w-full md:w-44">
                  <Select
                    options={FILTER_OPTIONS}
                    value={statusFilter}
                    onChange={val => { setStatusFilter(val); setPage(1); }}
                  />
                </div>
                <div className="w-full md:w-52">
                  <Select
                    options={VALUE_RANGE_OPTIONS}
                    value={valueRangeFilter}
                    onChange={val => { setValueRangeFilter(val); setPage(1); }}
                  />
                </div>
              </div>
            </div>

            {/* Bulk Actions Bar */}
            {selectedIds?.length > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-16 mb-16 flex items-center justify-between">
                <div className="flex items-center gap-10">
                  <Icon name="CheckSquare" size={16} className="text-violet-600" />
                  <span className="text-sm font-medium text-violet-900">
                    {selectedIds?.length} bundle{selectedIds?.length !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <div className="flex items-center gap-10">
                  <select
                    className="border border-violet-300 rounded-lg px-12 py-8 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/30 bg-white"
                    value={bulkStatus}
                    onChange={e => setBulkStatus(e?.target?.value)}
                  >
                    <option value="">Set status...</option>
                    <option value="active">Active</option>
                    <option value="draft">Draft</option>
                    <option value="sunset">Sunset</option>
                  </select>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleBulkStatusUpdate}
                    disabled={!bulkStatus}
                  >
                    Apply
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedIds([])}>
                    Clear
                  </Button>
                </div>
              </div>
            )}

            {/* Bundles Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-28 py-16 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-10">
                  <Icon name="Layers" size={18} className="text-violet-600" />
                  <span className="font-semibold text-slate-900">Solution Bundles</span>
                  <span className="text-xs bg-violet-100 text-violet-700 px-8 py-2 rounded-full font-medium">
                    {filtered?.length} bundle{filtered?.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-64">
                  <div className="flex flex-col items-center gap-16">
                    <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                    <p className="text-sm text-slate-500">Loading bundles catalog...</p>
                  </div>
                </div>
              ) : paginated?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-64 gap-16">
                  <div className="p-20 bg-slate-100 rounded-full">
                    <Icon name="Layers" size={40} className="text-slate-400" />
                  </div>
                  <div className="text-center">
                    <p className="text-slate-600 font-medium">No bundles found</p>
                    <p className="text-slate-400 text-sm mt-4">
                      {search || statusFilter || categoryFilter || valueRangeFilter ? 'Try adjusting your filters.' : 'Add the first solution bundle to the catalog.'}
                    </p>
                  </div>
                  {!showForm && !search && !statusFilter && !categoryFilter && !valueRangeFilter && (
                    <Button variant="default" iconName="Plus" iconPosition="left" iconSize={16} onClick={() => setShowForm(true)}>
                      Add First Bundle
                    </Button>
                  )}
                </div>
              ) : (
                <>
                  {/* Table Header */}
                  <div className="grid px-28 py-12 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider" style={{ gridTemplateColumns: '32px 40px 1fr 120px 180px 180px' }}>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                        checked={selectedIds?.length === paginated?.length && paginated?.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </div>
                    <div />
                    <div>Bundle</div>
                    <div className="text-center hidden md:block">Partners</div>
                    <div className="hidden lg:block pl-4">Value</div>
                    <div className="text-right">Actions</div>
                  </div>

                  <div className="divide-y divide-slate-100">
                    {paginated?.map(bundle => {
                      const pCount = partnerCounts?.[bundle?.id] || 0;
                      return (
                        <div key={bundle?.id} className="group">
                          <div className="grid items-start px-28 py-20 hover:bg-slate-50/60 transition-colors" style={{ gridTemplateColumns: '32px 40px 1fr 120px 180px 180px' }}>
                            {/* Checkbox */}
                            <div className="flex items-start pt-3">
                              <input
                                type="checkbox"
                                className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                                checked={selectedIds?.includes(bundle?.id)}
                                onChange={() => toggleSelect(bundle?.id)}
                              />
                            </div>

                            {/* Icon */}
                            <div className="flex items-start pt-2">
                              <div className="w-10 h-10 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg flex items-center justify-center">
                                <Icon name="Layers" size={18} className="text-violet-600" />
                              </div>
                            </div>

                            {/* Main Info */}
                            <div className="min-w-0 pr-16">
                              <div className="flex flex-wrap items-center gap-10 mb-6">
                                <button
                                  onClick={() => navigate(`/bundles-catalog/${bundle?.id}`)}
                                  className="font-semibold text-slate-900 hover:text-violet-600 transition-colors text-base text-left"
                                >
                                  {bundle?.bundleName}
                                </button>
                                {bundle?.bundleCategory && (
                                  <span className={`text-xs px-8 py-2 rounded-full font-medium ${getCategoryColor(bundle?.bundleCategory)}`}>
                                    {CATEGORY_OPTIONS?.find(c => c?.value === bundle?.bundleCategory)?.label || bundle?.bundleCategory}
                                  </span>
                                )}
                                <span className={`text-xs px-8 py-2 rounded-full font-medium ${getStatusColor(bundle?.catalogStatus)}`}>
                                  {bundle?.catalogStatus}
                                </span>
                              </div>

                              {bundle?.description && (
                                <p className="text-sm text-slate-500 mb-10 line-clamp-1">{bundle?.description}</p>
                              )}

                              <div className="flex flex-wrap gap-16 text-xs text-slate-500">
                                {bundle?.targetMarket && (
                                  <span className="flex items-center gap-6">
                                    <Icon name="Target" size={12} className="text-slate-400" />
                                    {bundle?.targetMarket}
                                  </span>
                                )}
                                {bundle?.includedProducts && (
                                  <span className="flex items-center gap-6">
                                    <Icon name="Package" size={12} className="text-slate-400" />
                                    {bundle?.includedProducts?.split(',')?.length} product{bundle?.includedProducts?.split(',')?.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                                {bundle?.includedServices && (
                                  <span className="flex items-center gap-6">
                                    <Icon name="Wrench" size={12} className="text-slate-400" />
                                    {bundle?.includedServices?.split(',')?.length} service{bundle?.includedServices?.split(',')?.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Partner Count */}
                            <div className="hidden md:flex items-start justify-center pt-1">
                              <button
                                onClick={() => navigate(`/bundles-catalog/${bundle?.id}?tab=partners`)}
                                className="flex flex-col items-center gap-2 group/count"
                                title="View linked partners"
                              >
                                <span className="text-lg font-bold text-violet-600 group-hover/count:text-violet-800 transition-colors">{pCount}</span>
                                <span className="text-xs text-slate-400">partners</span>
                              </button>
                            </div>

                            {/* Value */}
                            <div className="hidden lg:flex items-start pt-2 pl-4">
                              {bundle?.estimatedValue ? (
                                <span className="text-xs text-slate-600 font-medium leading-relaxed">{bundle?.estimatedValue?.replace(/\$/g, 'OMR ')?.replace(/OMR {2,}/g, 'OMR ')}</span>
                              ) : (
                                <span className="text-xs text-slate-300">—</span>
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-start justify-end gap-2 pt-1">
                              <button
                                onClick={() => navigate(`/bundles-catalog/${bundle?.id}`)}
                                className="p-8 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                                title="View profile"
                              >
                                <Icon name="ExternalLink" size={15} />
                              </button>
                              <button
                                onClick={() => setAssignModal(bundle)}
                                className="p-8 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                title="Assign to partner"
                              >
                                <Icon name="UserPlus" size={15} />
                              </button>
                              <button
                                onClick={() => handleClone(bundle)}
                                className="p-8 rounded-lg text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                                title="Clone bundle"
                              >
                                <Icon name="Copy" size={15} />
                              </button>
                              <button
                                onClick={() => handleEdit(bundle)}
                                className="p-8 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                title="Edit bundle"
                              >
                                <Icon name="Pencil" size={15} />
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(bundle?.id)}
                                className="p-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Delete bundle"
                              >
                                <Icon name="Trash2" size={15} />
                              </button>
                            </div>
                          </div>

                          {/* Delete Confirm */}
                          {deleteConfirm === bundle?.id && (
                            <div className="px-28 py-16 bg-red-50 border-t border-red-100 flex items-center justify-between">
                              <div className="flex items-center gap-10">
                                <Icon name="AlertTriangle" size={16} className="text-red-500" />
                                <span className="text-sm text-red-700 font-medium">
                                  Delete <strong>{bundle?.bundleName}</strong> from the catalog?
                                </span>
                              </div>
                              <div className="flex items-center gap-10">
                                <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  iconName="Trash2"
                                  iconPosition="left"
                                  iconSize={14}
                                  onClick={() => handleDelete(bundle?.id)}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Pagination */}
              {filtered?.length > PAGE_SIZE && (
                <div className="px-28 py-16 border-t border-slate-100">
                  <Pagination
                    currentPage={page}
                    totalPages={Math.ceil(filtered?.length / PAGE_SIZE)}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
